import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const STORAGE_KEY = 'b2b.cart.v1';

function readStorage(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Cart persisted in localStorage (no server-side cart table in v1).
 *
 * The storage key is scoped per customer (`b2b.cart.v1.<customer_id>`):
 * on a shared computer, logging in as another wholesale customer must
 * never expose the previous customer's cart — it carries their
 * negotiated prices (unit_price_snapshot).
 *
 * Each line snapshots the unit_price at add-to-cart time
 * (unit_price_snapshot). The submit path sends those snapshots to the
 * backend which compares against the live tier price and returns a 409
 * with drifted_lines when they diverge — see design doc §price drift.
 */
export function CartProvider({ children }) {
    const { user, loading: authLoading } = useAuth();
    const storageKey = user?.customer_id ? `${STORAGE_KEY}.${user.customer_id}` : null;
    const [items, setItems] = useState([]);
    const [hydratedKey, setHydratedKey] = useState(null);

    // (Re)hydrate once auth resolves and whenever the customer changes.
    useEffect(() => {
        if (authLoading) return;
        setItems(storageKey ? readStorage(storageKey) : []);
        setHydratedKey(storageKey);
    }, [storageKey, authLoading]);

    // Persist only after hydration for the current key, so the initial
    // empty state never wipes a stored cart.
    useEffect(() => {
        if (!storageKey || hydratedKey !== storageKey) return;
        try { localStorage.setItem(storageKey, JSON.stringify(items)); }
        catch {}
    }, [items, storageKey, hydratedKey]);

    const addItem = useCallback((product, quantity = 1) => {
        setItems((prev) => {
            const existing = prev.find((i) => i.product_id === product.id);
            if (existing) {
                // On rafraîchit aussi les métadonnées d'affichage : les paniers
                // déjà persistés dans localStorage ont été écrits avant que la
                // vignette existe et n'ont pas d'`image_url`. Le prix, lui, reste
                // le snapshot d'origine — c'est lui que le contrôle de dérive
                // compare au submit.
                return prev.map((i) =>
                    i.product_id === product.id
                        ? {
                            ...i,
                            name: product.name ?? i.name,
                            sku: product.sku ?? i.sku,
                            image_url: product.image_url ?? i.image_url ?? null,
                            quantity: i.quantity + quantity,
                        }
                        : i
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    sku: product.sku,
                    name: product.name,
                    image_url: product.image_url ?? null,
                    unit_price_snapshot: Number(product.unit_price ?? 0),
                    case_pack: Number(product.case_pack ?? 1) || 1,
                    quantity,
                },
            ];
        });
    }, []);

    const updateQuantity = useCallback((productId, quantity) => {
        setItems((prev) =>
            prev
                .map((i) => (i.product_id === productId ? { ...i, quantity } : i))
                .filter((i) => i.quantity > 0)
        );
    }, []);

    const removeItem = useCallback((productId) => {
        setItems((prev) => prev.filter((i) => i.product_id !== productId));
    }, []);

    const replaceSnapshots = useCallback((updates) => {
        // After a price-drift 409, the user accepts the new prices and
        // we rewrite the cart snapshots in place so the next submit
        // passes.
        setItems((prev) =>
            prev.map((i) => {
                const u = updates.find((d) => d.product_id === i.product_id);
                return u ? { ...i, unit_price_snapshot: u.new_price } : i;
            })
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const totals = useMemo(() => {
        const subtotal = items.reduce((sum, i) => sum + i.unit_price_snapshot * i.quantity, 0);
        const count = items.reduce((sum, i) => sum + i.quantity, 0);
        return {
            subtotal: Math.round(subtotal * 100) / 100,
            distinctLines: items.length,
            count,
        };
    }, [items]);

    const value = useMemo(
        () => ({ items, totals, addItem, updateQuantity, removeItem, replaceSnapshots, clear }),
        [items, totals, addItem, updateQuantity, removeItem, replaceSnapshots, clear]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used within CartProvider');
    return ctx;
}
